import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Users } from "lucide-react";
import { api } from "../lib/api";

/**
 * Admin demand-signal widget: shows pending back-in-stock subscriptions
 * grouped by (product, size). Helps the owner prioritize what to restock first.
 */
export default function RestockWaitlist() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    api.get("/admin/restock-waitlist")
      .then(({ data }) => setRows(data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (rows.length === 0) return null;

  const totalWaiting = rows.reduce((sum, r) => sum + r.waiting_count, 0);
  const visible = expanded ? rows : rows.slice(0, 4);

  return (
    <div
      data-testid="restock-waitlist"
      className="mt-4 bg-[#9c6dde]/8 border-2 border-[#9c6dde]/40 rounded-2xl p-5"
    >
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <div className="flex items-center gap-2.5">
          <Bell size={16} className="text-[#7d52b8]"/>
          <div>
            <div className="font-display font-extrabold text-[#1c1815] text-base">Restock Waitlist</div>
            <div className="text-[11px] text-[#7d52b8] font-bold uppercase tracking-[0.18em]">
              <Users size={10} className="inline align-text-bottom mr-1"/>
              {totalWaiting} shopper{totalWaiting === 1 ? "" : "s"} waiting · {rows.length} item{rows.length === 1 ? "" : "s"}
            </div>
          </div>
        </div>
      </div>
      <div className="space-y-2">
        {visible.map((r) => {
          const stillOos = r.current_stock <= 0;
          return (
            <Link
              key={`${r.product_id}-${r.variant_size || "main"}`}
              to={`/product/${r.slug}`}
              target="_blank"
              data-testid={`waitlist-row-${r.product_id}-${r.variant_size || "main"}`}
              className="flex items-center gap-3 p-2.5 bg-white border border-[#e5dccd] rounded-xl hover:border-[#9c6dde] hover:shadow-sm transition-all"
            >
              <img src={r.image_url} alt="" className="w-12 h-12 object-cover rounded-lg flex-shrink-0"/>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-[#1c1815] truncate text-sm" title={r.title}>{r.title}</div>
                <div className="mt-0.5 flex items-center gap-1.5 text-[11px] flex-wrap">
                  {r.variant_size && (
                    <span className="bg-[#faf4e9] text-[#1c1815] px-2 py-0.5 rounded-full font-bold uppercase tracking-[0.14em] text-[10px]">
                      Size {r.variant_size}
                    </span>
                  )}
                  <span className={`uppercase tracking-[0.14em] font-bold text-[10px] ${stillOos ? "text-[#c93875]" : "text-[#1f8d83]"}`}>
                    {stillOos ? "Sold Out" : `${r.current_stock} in stock`}
                  </span>
                </div>
              </div>
              <div className="flex-shrink-0 text-right">
                <div className="font-display font-extrabold text-2xl text-[#7d52b8] leading-none">{r.waiting_count}</div>
                <div className="text-[9px] uppercase tracking-[0.18em] text-[#8a8076] font-bold mt-0.5">waiting</div>
              </div>
            </Link>
          );
        })}
      </div>
      {rows.length > 4 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid="waitlist-toggle"
          className="mt-3 text-[11px] font-bold uppercase tracking-[0.18em] text-[#7d52b8] hover:text-[#5b3aa0]"
        >
          {expanded ? "Show less" : `Show ${rows.length - 4} more`}
        </button>
      )}
    </div>
  );
}
